import clx from 'classnames';
import DOMPurify from 'dompurify';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useBooleanConditions } from '../../../../hooks/useBooleanConditions.tsx';
import { HtmlInlineText } from '../../../inputs/common/option-list/style/HtmlInlineText.tsx';
import { type IWidgetParsedProps } from '../../../inputs/common/util/getWidgetProps.ts';
import { type ITableRowErrorable } from '../ITableRow.ts';
import { $TableInputWidgetRowsTable } from '../style/TableInputWidgetRowsTable.style.tsx';
import { type ITableInputWidgetProps } from '../TableInputWidgetComponent.tsx';
import { useTableInputWidgetColumns } from './useTableInputWidgetColumns.tsx';

// Stilizziamo una Colonna per mantenere il padding
// l'elemento sarà renderizzato in un componente Row (come gli altri widget)
// TODO andrà rivista questa soluzione eliminando il componente <Col /> dal widget
const TableInputContainerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  // eliminiamo lo spazio sotto il titolo
  // è duplicato in preview dal container dei widget
  padding-bottom: 12px;
`;

/**
 * Widget tabella di input
 * Props condivise con il componente pricipale
 */
export function TableInputContainer(
  props: ITableInputWidgetProps & { options: IWidgetParsedProps },
) {
  const { widget, options } = props;
  const { rows } = widget;

  const rowsVisibilites = useBooleanConditions(
    rows.map(row => row.visible),
    { name: options.name, preview: options.preview },
  );

  // duplichiamo le righe per creare per ogni riga
  // una riga supplementare in cui mostrare gli errori
  const rowsWithErrors = useMemo(
    () =>
      rows
        .filter((r, i) => rowsVisibilites[i].result)
        .flatMap((row: ITableRowErrorable) => [
          row,
          {
            ...row,
            id: row.id + '-error',
            isError: true,
          } as ITableRowErrorable,
        ]),
    [rows, rowsVisibilites],
  );

  const columns = useTableInputWidgetColumns(widget, rowsWithErrors, options);

  const title = useMemo(
    () => widget.layout?.showTableTitle && widget.layout?.tableTitle,
    [widget.layout],
  );

  const getRowClassName = useCallback(
    (row: ITableRowErrorable) =>
      clx('table-row', {
        'table-input-row-error': row?.isError,
        'table-input-row': !row?.isError,
      }),
    [],
  );

  return (
    <TableInputContainerWrapper>
      <$TableInputWidgetRowsTable
        tableLayout="fixed"
        className={clx({
          'table-input-light-header': widget.layout?.lightTableHeader,
        })}
        caption={
          title && (
            <HtmlInlineText
              // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitize is safe
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(title ?? ''),
              }}
            />
          )
        }
        rowClassName={getRowClassName}
        showHeader={!widget?.layout?.hideTableHeader}
        dataSource={rowsWithErrors}
        columns={columns}
        size="small"
        pagination={false}
        rowKey={'id'}
      />
    </TableInputContainerWrapper>
  );
}
